import { CustomState } from "@iewc/Store/Reducers";
import { CustomCatalogSetting } from "@iewc/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitShippingDetails";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { addToChainAfter, executeAwaitableHandlerChain, replaceInChain } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import loadCatalogPageByPath from "@insite/client-framework/Store/Data/CatalogPages/Handlers/LoadCatalogPageByPath";
import {
    chain,
    LoadRealTimeInventory,
    ParseQueryParameter,
    PopulateApiParameter,
    PrepageProductRecs,
    RequestCatalogPageFromApi,
    SetExpandTokens,
    SetUpProductInfos,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import qs from "qs";

let view = "List";
let tablePage = 1;

function initializeView(catalogPage: any) {
    if (
        catalogPage &&
        (catalogPage.properties?.productAttributes?.length > 0 || catalogPage.properties?.productProperties?.length > 0)
    ) {
        view = "Table";
    } else {
        view = "List";
    }
}

replaceInChain(chain, RequestCatalogPageFromApi, async props => {
    const { path, isSearch } = props.parameter;
    props.result = {
        productFilters: {},
        isFiltered: false,
        productInfosByProductId: {},
    };

    if (path && !isSearch) {
        props.result.catalogPage = getCatalogPageStateByPath(props.getState(), path).value;
        if (props.result.catalogPage) {
            return;
        }
        props.result.catalogPage = await executeAwaitableHandlerChain(loadCatalogPageByPath, { path }, props);
        await initializeView(props.result.catalogPage);
    }
});

addToChainAfter(chain, SetExpandTokens, props => {
    // Verndale Customization
    const state = props.getState() as CustomState;
    props.expandTokens?.push("properties", "documents");

    if (state.customProductList.page) {
        props.expandTokens = props.expandTokens?.filter(token => token !== "variantTraits");
    }
    // END Verndale Customization
});

replaceInChain(chain, ParseQueryParameter, props => {
    const state = props.getState() as CustomState;
    const settingsCollection = getSettingsCollection(state);
    const customCatalogSettings = settingsCollection?.["customCatalogSettings"] as CustomCatalogSetting;

    const { queryString } = props.parameter;
    const parsedQuery = qs.parse(queryString.startsWith("?") ? queryString.substr(1) : queryString);

    const splitCommaSeparated = (value?: string | string[] | null) =>
        value && typeof value === "string" ? value.split(",") : undefined;
    const splitSpaceSeparated = (value?: string | string[] | null) =>
        value && typeof value === "string" ? value.split(" ") : undefined;

    const {
        criteria,
        query,
        page,
        sort,
        includeSuggestions,
        stockedItemsOnly,
        previouslyPurchasedProducts,
        categoryId,
        brandIds,
        productLineIds,
        priceFilters,
        attributeValueIds,
        searchWithinQueries,
    } = parsedQuery;

    const { catalogPage } = props.result;

    // we update the url if view is table, so we need to remove the page and pageSize from the query,
    // and we save the page and pageSize in the customProductList state
    if (view === "Table" && parsedQuery.page) {
        // if parsedQuery.page is not a number or is NaN, we set it to 1
        tablePage = !Number.isNaN(Number(parsedQuery.page)) ? parseInt(parsedQuery.page as string, 10) : 1;

        props.dispatch({
            type: "Custom/ProductList/SetPage",
            page: tablePage,
        } as any);
    }

    const catalogPageSize =
        view === "Table"
            ? customCatalogSettings?.["categoriesTablePageSize"]
            : (getCookie("ProductList-PageSize") as string) || customCatalogSettings?.["categoriesPageSize"];

    let pageSize = catalogPageSize || parsedQuery.pageSize;

    if (!pageSize) {
        pageSize = getCookie("ProductList-PageSize");
    }

    props.result.productFilters = {
        query: criteria || query,
        page: view === "Table" ? undefined : page ? parseInt(page as string, 10) : undefined,

        pageSize: view === "Table" ? undefined : parseInt(pageSize as string, 10),
        sort,
        includeSuggestions: includeSuggestions !== undefined ? includeSuggestions === "true" : undefined,
        stockedItemsOnly: stockedItemsOnly !== undefined ? stockedItemsOnly === "true" : undefined,
        previouslyPurchasedProducts:
            previouslyPurchasedProducts !== undefined ? previouslyPurchasedProducts === "true" : undefined,
        pageCategoryId: catalogPage?.categoryId,
        pageBrandId: catalogPage?.brandId || undefined,
        pageProductLineId: catalogPage?.productLineId || undefined,
        categoryId,
        brandIds: splitCommaSeparated(brandIds),
        productLineIds: splitCommaSeparated(productLineIds),
        priceFilters: splitCommaSeparated(priceFilters),
        attributeValueIds: splitCommaSeparated(attributeValueIds),
        searchWithinQueries: splitSpaceSeparated(searchWithinQueries), // Search within Queries need to split by space, to use for ElasticSearch
    };
});

replaceInChain(chain, PopulateApiParameter, props => {
    const state = props.getState() as CustomState;

    const settingsCollection = getSettingsCollection(state);
    const customCatalogSettings = settingsCollection?.["customCatalogSettings"] as CustomCatalogSetting;

    const catalogPageSize =
        view === "Table"
            ? customCatalogSettings?.["categoriesTablePageSize"]
            : customCatalogSettings?.["categoriesPageSize"];

    const filters = props.result.productFilters;

    const {
        query,
        brandIds,
        productLineIds,
        priceFilters,
        attributeValueIds,
        searchWithinQueries,
        pageCategoryId,
        pageBrandId,
        pageProductLineId,
        ...apiParameter
    } = filters;
    props.apiParameter = {
        ...apiParameter,
        page: view === "Table" ? tablePage : filters.page, // we silently send the page from the state to the api
        pageSize: view === "Table" ? +catalogPageSize : filters.pageSize, // we silently send the pageSize from the state to the api
        search: query,
        categoryId: filters.categoryId || filters.pageCategoryId,
        searchWithin: searchWithinQueries?.join(" "),
        includeSuggestions: filters.includeSuggestions !== false,
        brandIds: filters.pageBrandId ? [filters.pageBrandId] : brandIds,
        productLineIds: filters.pageProductLineId ? [filters.pageProductLineId] : productLineIds,
        priceFilters,
        attributeValueIds,
        expand: props.expandTokens || [],
        applyPersonalization: true,
        includeAttributes: ["includeOnProduct"],
        relevancy: props.parameter.relevancy,
    };
});

replaceInChain(chain, SetUpProductInfos, props => {
    // Verndale Customization
    const state = props.getState() as CustomState;
    let products: ProductModel[] = [];

    if (view === "Table" && tablePage > 1) {
        products = [...props.result.products!, ...state.customProductList.products!];
    } else {
        products = props.result.products!;
    }
    // END Verndale Customization

    const categoryPath = props.apiParameter.search ? undefined : props.result.catalogPage?.canonicalPath;

    props.idByPath = {};

    products?.forEach(product => {
        const productDetailPath = categoryPath ? `${categoryPath}/${product.urlSegment}` : product.canonicalUrl;
        props.idByPath![productDetailPath] = product.id;
        props.result.productInfosByProductId[product.id] = {
            ...createFromProduct(product),
            productDetailPath,
        };
    });
});

replaceInChain(chain, LoadRealTimeInventory, props => {
    // Verndale Customization
    const state = props.getState() as CustomState;
    let products: ProductModel[] = [];

    if (view === "Table" && tablePage > 1) {
        products = [...props.result.products!, ...state.customProductList.products!];
    } else {
        products = props.result.products!;
    }
    // END Verndale Customization

    if (
        !products?.length ||
        (getIsWebCrawler() &&
            !getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds: products.map(o => o.id),
            onSuccess: realTimeInventory => {
                props.dispatch({
                    type: "Pages/ProductList/CompleteLoadRealTimeInventory",
                    realTimeInventory,
                });
            },
            onError: () => {
                props.dispatch({
                    type: "Pages/ProductList/FailedLoadRealTimeInventory",
                });
            },
            onComplete(realTimeInventoryProps) {
                if (realTimeInventoryProps.apiResult) {
                    this.onSuccess?.(realTimeInventoryProps.apiResult);
                } else if (realTimeInventoryProps.error) {
                    this.onError?.(realTimeInventoryProps.error);
                }
            },
        }),
    );
});

addToChainAfter(chain, PrepageProductRecs, props => {
    // Verndale Customization
    const state = props.getState() as CustomState;

    if (view === "Table") {
        const {
            query,
            stockedItemsOnly,
            previouslyPurchasedProducts,
            brandIds,
            productLineIds,
            priceFilters,
            attributeValueIds,
            searchWithinQueries,
        } = props.result.productFilters;

        const properties = [
            query,
            stockedItemsOnly,
            previouslyPurchasedProducts,
            brandIds,
            productLineIds,
            priceFilters,
            attributeValueIds,
            searchWithinQueries,
        ];

        const isAnyPropertyFilled = properties.some(property => {
            if (Array.isArray(property)) {
                return property.length > 0;
            }
            if (typeof property === "string") {
                return property !== "";
            }
            if (typeof property === "boolean") {
                return property === true;
            }

            // If the property is none of the above types, consider it not filled
            return false;
        });

        const isAllPropertiesEmpty = properties.every(property => {
            if (Array.isArray(property)) {
                return property.length === 0;
            }
            if (typeof property === "string") {
                return property === "";
            }
            if (typeof property === "boolean") {
                return property === false;
            }

            // If the property is none of the above types, consider it empty
            return true;
        });

        if (
            tablePage === 1 ||
            (isAnyPropertyFilled && !state.customProductList.isLoading) ||
            (isAllPropertiesEmpty && !state.customProductList.isLoading && tablePage > 1)
        ) {
            props.dispatch({
                type: "Custom/ProductList/ResetInfiniteScrollPage",
            } as any);
        }

        props.dispatch({
            type: "Custom/ProductList/SetProducts",
            products: props.result.products,
        } as any);

        props.dispatch({
            type: "Custom/ProductList/InfiniteScrollLoaded",
        } as any);
    }

    props.dispatch({
        type: "Custom/ProductList/SetFilterLoading",
        isLoadingFilter: false,
    } as any);

    // END Verndale Customization
});
